import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, RotateCcw, Download, Loader2 } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { ShipTag, markShip } from "@/components/HigherShipTracker";

export const DomsAnswer = ({ d, onReopen, showName, selected, onSelect }) => (
  <div data-testid={`doms-answer-${d.joiner_id}`} className={`rounded-xl border px-4 py-3 text-sm ${d.photos_verified ? "border-white/10 bg-zinc-900/60" : "border-red-500/40 bg-red-500/5"}`}>
    <div className="flex flex-wrap items-center gap-2">
      {onSelect && <Checkbox data-testid={`doms-select-${d.joiner_id}`} checked={selected} onCheckedChange={() => onSelect(d.joiner_id)} aria-label={`Select ${d.joiner_name}`} />}
      {showName && <span className="font-semibold">{d.joiner_name}</span>}
      <ShipTag status={d.ship_status} testId={`doms-ship-tag-${d.joiner_id}`} />
      {!d.photos_verified && <span data-testid={`doms-flag-${d.joiner_id}`} className="inline-flex items-center gap-1 rounded-full bg-red-500/15 border border-red-500/40 px-2 py-0.5 text-[11px] font-bold text-red-300"><AlertTriangle className="h-3 w-3" /> Photos not verified</span>}
      {d.unlocked && <span className="rounded-full border border-amber-400/40 px-2 py-0.5 text-[11px] text-amber-200">Reopened</span>}
      <span className="ml-auto text-xs text-zinc-500">{fmtDate(d.submitted_at)}</span>
    </div>
    <div className="mt-2 whitespace-pre-wrap break-words text-zinc-200" data-testid={`doms-address-${d.joiner_id}`}>{d.address}</div>
    <div className="mt-1 text-xs text-sky-300" data-testid={`doms-method-${d.joiner_id}`}>{d.method_label}</div>
    {!d.unlocked && (
      <button type="button" data-testid={`doms-reopen-${d.joiner_id}`} onClick={() => onReopen(d)}
        className="mt-2 inline-flex items-center gap-1 rounded-full border border-zinc-700 px-2.5 py-1 text-xs text-zinc-300 transition-colors hover:border-amber-400/60 hover:text-white">
        <RotateCcw className="h-3 w-3" /> Reopen form
      </button>
    )}
  </div>
);

export const reopenDoms = async (d, done) => {
  if (!window.confirm(`Reopen ${d.joiner_name}'s DoMs form so they can send it again?`)) return;
  try {
    await api.post(`/higher/joiners/${d.joiner_id}/doms/reopen`);
    toast.success("DoMs form reopened");
    done();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const ExportButton = ({ disabled }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/higher/doms/export", { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || "higher-doms.csv";
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e, "Couldn't download spreadsheet"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" data-testid="doms-export-btn" disabled={disabled || busy} onClick={download}
      className="inline-flex items-center gap-1.5 rounded-full border border-sky-400/40 px-3 py-1.5 text-xs font-semibold text-sky-200 transition-colors hover:bg-sky-500/10 disabled:cursor-not-allowed disabled:opacity-40">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download spreadsheet
    </button>
  );
};

const BulkBar = ({ rows, picked, setPicked, done }) => {
  const all = rows.length > 0 && picked.length === rows.length;
  const run = (s) => markShip(picked, s, () => { setPicked([]); done(); });
  return (
    <div data-testid="doms-bulk-bar" className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-950/60 px-3 py-2">
      <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
        <Checkbox data-testid="doms-select-all" checked={all} onCheckedChange={() => setPicked(all ? [] : rows.map((r) => r.joiner_id))} /> Select all
      </label>
      <span data-testid="doms-selected-count" className="text-xs text-zinc-500">{picked.length} selected</span>
      <div className="ml-auto flex gap-2">
        {[["packed", "Mark Packed"], ["shipped", "Mark Shipped"]].map(([s, label]) => (
          <button key={s} type="button" data-testid={`doms-bulk-${s}`} disabled={!picked.length} onClick={() => run(s)}
            className="rounded-full border border-sky-400/40 px-3 py-1 text-xs font-semibold text-sky-200 transition-colors hover:bg-sky-500/10 disabled:cursor-not-allowed disabled:opacity-40">{label}</button>
        ))}
      </div>
    </div>
  );
};

export default function HigherDomsAdmin({ joinerId, refreshKey, onChanged }) {
  const [rows, setRows] = useState(null);
  const [ver, setVer] = useState(0);
  const [picked, setPicked] = useState([]);
  useEffect(() => {
    api.get("/higher/doms", { params: joinerId ? { joiner_id: joinerId } : {} }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [joinerId, refreshKey, ver]);
  const done = () => { setVer((v) => v + 1); onChanged?.(); };
  if (joinerId) {
    if (!rows?.length) return null;
    return <section className="mt-6" data-testid="doms-admin-joiner"><h2 className="font-display text-base font-bold mb-2">DoMs Form <span className="text-xs font-normal text-zinc-500">(admin only)</span></h2><DomsAnswer d={rows[0]} onReopen={(d) => reopenDoms(d, done)} /></section>;
  }
  const flagged = (rows || []).filter((d) => !d.photos_verified).length;
  return (
    <section className="mt-12" data-testid="doms-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-base md:text-lg font-bold">
          DoMs Forms <span className="font-mono text-sm text-zinc-500">({rows?.length ?? 0})</span>
          {flagged > 0 && <span data-testid="doms-flagged-count" className="ml-2 rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-bold text-white align-middle">{flagged} not verified</span>}
        </h2>
        <ExportButton disabled={!rows?.length} />
      </div>
      <p className="text-xs text-zinc-500 mt-1">Admin only. Mailing addresses and DoM choices from Higher Joiners. Tick joiners to mark them Packed or Shipped together.</p>
      {rows?.length > 0 && <BulkBar rows={rows} picked={picked} setPicked={setPicked} done={done} />}
      <div className="mt-3 space-y-2">
        {rows?.length === 0 && <div data-testid="doms-empty" className="rounded-xl border border-dashed border-zinc-800 px-4 py-5 text-sm text-zinc-500">No DoMs forms yet.</div>}
        {rows?.map((d) => <DomsAnswer key={d.joiner_id} d={d} showName onReopen={(x) => reopenDoms(x, done)} selected={picked.includes(d.joiner_id)}
          onSelect={(id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))} />)}
      </div>
    </section>
  );
}
